/**
 * NetWorth section (rewritten in Patch BN).
 *
 * Renders the top-level Net worth area on the Dashboard. Three pieces
 * stacked inside one <section>:
 *
 *   1. Net worth headline + equation tile (Assets - Liabilities = net worth)
 *   2. Assets sub-card: pie chart breaking down what the user owns.
 *      Slices include direct BTC, Bitcoin ETFs, non-Bitcoin securities,
 *      metals (spot + ETFs), real estate (at full market value), and cash.
 *   3. Liabilities sub-card: pie chart for what the user owes.
 *      Slices include the Bitcoin-backed loan balance (computed from
 *      events + loan-funded purchases with daily compounding), real
 *      estate mortgages (computed from amortization-on-render), and
 *      debt items (credit cards, student loans, etc.).
 *
 * Color rules per Paul's spec:
 *   - BTC + Bitcoin ETFs: Bitcoin orange family (var(--accent), HSL ramp)
 *   - Stocks/non-Bitcoin ETFs/mutual funds/bonds/commodities: grayscale,
 *     by display order, cycling through the 8-shade palette
 *   - Metals: per-metal native color (gold/silver/platinum/palladium),
 *     fall back to silver for unknown
 *   - Real estate: beige ramp, one shade per property
 *   - Cash: pure white
 *   - BTC loan (liability): Bitcoin orange (same family as BTC purchases)
 *   - Mortgages (liability): matching property's beige shade
 *   - Other debt: red ramp, by display order
 *
 * Real estate balance-sheet treatment: full home value goes in Assets,
 * full outstanding mortgage goes in Liabilities. Net worth = sum of
 * assets - sum of liabilities. Standard accounting; lets the user see
 * both numbers separately and model future changes (price drop vs
 * paydown) independently.
 */

import { useMemo, lazy, Suspense } from 'react';
import { useAppStore } from '../state/store.js';
import { Decimal, ZERO } from '../engine/money.js';
import { formatUsd, formatBtc } from './Money.js';
import { holdingPosition } from './EtfHoldings.js';
import { computeLoanState } from '../engine/loan-state.js';
import { currentMortgageBalance } from '../engine/mortgage.js';
import type { RealEstateProperty, DebtItemType } from '../engine/types.js';
import type { AssetSlice, LiabilitySlice } from './NetWorthChart.js';

// Patch DL: lazy-load the two pie charts so the ~100 KB gzip charts
// chunk does not preload on Dashboard cold start. Both calls share
// one chunk download (the second dynamic import resolves to the
// cached promise from the first). The chart components manage their
// own hover state internally so no setter threading is needed across
// the lazy boundary.
const LazyAssetsChart = lazy(() => import('./NetWorthChart.js').then(m => ({ default: m.NetWorthAssetsChart })));
const LazyLiabilitiesChart = lazy(() => import('./NetWorthChart.js').then(m => ({ default: m.NetWorthLiabilitiesChart })));

// ---------- color palettes ----------

/** Grayscale shades used for stocks / non-Bitcoin ETFs / mutual funds /
 *  bonds / commodity ETFs / crypto ETFs. Assigned by display order;
 *  cycles when more entries than shades. 8 shades alternating dark and
 *  light so adjacent slices stay distinguishable even when the pie has
 *  many entries. */
const GRAYSCALE_SHADES = [
  '#a8a8a8', '#808080', '#5a5a5a', '#c4c4c4',
  '#6f6f6f', '#919191', '#4a4a4a', '#d6d6d6',
];

/** Native metal colors. Spot tickers (XAUUSD=X et al) and ETFs both
 *  route through here based on the symbol prefix / name. */
const METAL_COLOR_BY_KEY: Record<string, string> = {
  gold:      '#d4af37',
  silver:    '#c0c0c0',
  platinum:  '#e5e4e2',
  palladium: '#ced0dd',
};

/** Beige ramp for real estate. Each property gets its own slot; cycles
 *  for 5+ properties. Same shade is reused for that property's mortgage
 *  in the Liabilities pie so the user can mentally pair them. */
const BEIGE_SHADES = [
  '#c2a878', '#a88e60', '#d8c094', '#8e7548', '#b89967',
];

/** Red ramp for non-mortgage debt (credit cards, student loans, etc.). */
const RED_SHADES = [
  '#c14747', '#a83a3a', '#d76060', '#8b2c2c',
  '#e07878', '#933030', '#b65454',
];

/** Bitcoin-loan slice color in the Liabilities pie. Per Paul's spec,
 *  "same color as the dashboard chart above showing btc purchases" =
 *  Bitcoin orange. Keeps visual continuity: the loan is in the BTC
 *  family because it's BTC-backed. */
const BTC_LOAN_COLOR = 'var(--accent)';

/** Bitcoin ETF lightness ramp (matches the pre-BN behavior so this
 *  doesn't visually drift). Lightness 60% + 12% per index, capped at 88%
 *  to avoid near-white slices. */
function getBitcoinEtfColor(etfIndex: number): string {
  const lightness = Math.min(60 + etfIndex * 12, 88);
  return `hsl(29, 93%, ${lightness}%)`;
}

/** Identify metal from symbol or name. Used to color metal-related
 *  slices in their native color. */
function metalKey(symbol: string, name: string): string {
  const s = symbol.toUpperCase();
  const n = name.toLowerCase();
  if (s.startsWith('XAU') || s === 'GLD' || s === 'IAU' || s === 'SGOL' || s === 'GLDM' || s === 'BAR' || n.includes('gold')) return 'gold';
  if (s.startsWith('XAG') || s === 'SLV' || s === 'SIVR' || s === 'PSLV' || n.includes('silver')) return 'silver';
  if (s.startsWith('XPT') || s === 'PPLT' || n.includes('platinum')) return 'platinum';
  if (s.startsWith('XPD') || s === 'PALL' || n.includes('palladium')) return 'palladium';
  return 'silver'; // fallback per spec
}

// Patch DL: metalLabel moved to NetWorthChart.tsx (only the tooltip
// rendering uses it). debtTypeLabel kept here because slice name
// computation below uses it for the default label when a debt item
// has no user-provided label; the tooltip in NetWorthChart.tsx has its
// own copy of the same function. The duplication is small (5 cases)
// and keeps NetWorth.tsx from pulling the chart module's runtime
// across the lazy boundary just to compute slice names.

/** Pretty label for debt type, used in slice name fallback below
 *  (when a debt item has no user-provided label) and in the
 *  Liabilities tooltip (NetWorthChart.tsx has its own copy). */
function debtTypeLabel(t: DebtItemType): string {
  switch (t) {
    case 'credit_card': return 'Credit card';
    case 'student_loan': return 'Student loan';
    case 'auto_loan': return 'Auto loan';
    case 'personal_loan': return 'Personal loan';
    case 'other': return 'Other debt';
  }
}

const GRAMS_PER_TROY_OZ = new Decimal('31.1034768');

// ---------- slice shapes ----------

// Patch DL: AssetCategory, LiabilityCategory, AssetSlice, LiabilitySlice
// types moved to NetWorthChart.tsx and imported via `import type` above.
// `import type` is erased at compile time so this file still does not
// trigger a runtime load of the chart module.

// Patch DL: renderActiveShape moved to NetWorthChart.tsx along with the
// PieChart JSX that uses it.

// ===========================================================================
// Main component
// ===========================================================================

export function NetWorth(): JSX.Element {
  // ---- core state slices ----
  const purchases = useAppStore(s => s.purchases);
  const liveSnapshot = useAppStore(s => s.liveSnapshot);
  const holdings = useAppStore(s => s.etfHoldings);
  const txs = useAppStore(s => s.etfTransactions);
  const loanEvents = useAppStore(s => s.loanEvents);
  const securityAssets = useAppStore(s => s.securityAssets);
  const securityTxs = useAppStore(s => s.securityTransactions);
  const realEstateProperties = useAppStore(s => s.realEstateProperties);
  const cashEntries = useAppStore(s => s.cashEntries);
  const debtItems = useAppStore(s => s.debtItems);

  // ---- provider-aware APR for loan derivation (preserved from pre-BN) ----
  const selectedProvider = useAppStore(s => s.selectedLoanProvider);
  const aaveSnapshot = useAppStore(s => s.aaveSnapshot);
  const lednSnapshot = useAppStore(s => s.lednSnapshot);
  const manualOverride = useAppStore(s => s.manualBorrowAprOverride);

  const btcPriceUsd = liveSnapshot?.priceUsd ?? ZERO;
  const havePrice = btcPriceUsd.greaterThan(0);

  // ---- compute Bitcoin totals ----
  const directBtc = purchases.reduce((acc, p) => acc.plus(p.btc), ZERO);
  const directUsd = directBtc.times(btcPriceUsd);

  let etfUsd = ZERO;
  for (const h of holdings) {
    const pos = holdingPosition(h, txs);
    etfUsd = etfUsd.plus(pos.currentShares.times(pos.effectivePriceUsd));
  }

  // ---- compute securities + metals slices ----
  // For each SecurityAsset, balance = sum(buys.qty) - sum(sells.qty),
  // value = balance x current price. Spot metals: price is per-gram
  // (Yahoo gives per-oz; convert via GRAMS_PER_TROY_OZ).
  const { securityPieData, metalPieData } = useMemo(() => {
    const securities: AssetSlice[] = [];
    const metals: AssetSlice[] = [];
    for (const asset of securityAssets) {
      let qty = ZERO;
      for (const t of securityTxs) {
        if (t.assetId !== asset.id) continue;
        qty = t.direction === 'buy' ? qty.plus(t.quantity) : qty.minus(t.quantity);
      }
      if (qty.lessThanOrEqualTo(0)) continue;
      const isSpotMetal = asset.assetClass === 'spot_metal';
      const rawPrice = asset.manualPriceUsd ?? asset.livePriceSnapshot?.priceUsd ?? null;
      if (!rawPrice) continue;
      const unitPrice = isSpotMetal ? rawPrice.div(GRAMS_PER_TROY_OZ) : rawPrice;
      const valueUsd = qty.times(unitPrice);
      if (valueUsd.lessThanOrEqualTo(0)) continue;

      const isMetal = asset.assetClass === 'metal_etf' || asset.assetClass === 'spot_metal';
      const slice: AssetSlice = {
        id: asset.id,
        name: asset.name,
        category: isMetal ? 'metal' : 'security',
        subKey: isMetal ? metalKey(asset.symbol, asset.name) : asset.symbol,
        value: valueUsd.toNumber(),
        quantity: qty,
        quantityUnit: isSpotMetal ? 'g' : 'shares',
        pricePerUnit: unitPrice,
      };
      if (isMetal) metals.push(slice);
      else securities.push(slice);
    }
    return { securityPieData: securities, metalPieData: metals };
  }, [securityAssets, securityTxs]);

  // ---- compute totals for sub-card headers ----
  const securitiesUsd = securityPieData.reduce((s, p) => s.plus(new Decimal(p.value)), ZERO);
  const metalsUsd = metalPieData.reduce((s, p) => s.plus(new Decimal(p.value)), ZERO);
  const realEstateUsd = realEstateProperties.reduce(
    (s, p) => s.plus(p.currentValueUsd), ZERO,
  );
  const cashUsd = cashEntries.reduce((s, c) => s.plus(c.amountUsd), ZERO);

  // ---- compute liabilities ----
  // BTC loan: provider-aware APR, computeLoanState walks events with
  // daily compounding to right now.
  const activeProviderSnap = selectedProvider === 'aave' ? aaveSnapshot : lednSnapshot;
  const fallbackProviderSnap = selectedProvider === 'aave' ? lednSnapshot : aaveSnapshot;
  const fallbackApr =
    manualOverride
    ?? activeProviderSnap?.borrowApr
    ?? fallbackProviderSnap?.borrowApr
    ?? new Decimal('0.06');
  const loanState = computeLoanState({
    purchases,
    loanEvents,
    asOf: new Date(),
    fallbackApr,
  });
  const btcLoanUsd = loanState.currentBalance;

  // Mortgages: compute current balance per property via amortization
  // rolled forward from each mortgage's balanceAsOfDate to today.
  const asOfDate = new Date();
  interface MortgageSliceData {
    property: RealEstateProperty;
    propertyIdx: number;
    currentBalanceUsd: Decimal;
  }
  const mortgageSlicesData: MortgageSliceData[] = useMemo(() => {
    const out: MortgageSliceData[] = [];
    realEstateProperties.forEach((property, propertyIdx) => {
      if (!property.mortgage) return;
      const balance = currentMortgageBalance(property.mortgage, asOfDate);
      if (balance.lessThanOrEqualTo(0)) return;
      out.push({ property, propertyIdx, currentBalanceUsd: balance });
    });
    return out;
    // asOfDate intentionally captured per-render; mortgage balance is
    // a function of "right now" and we want it to update whenever the
    // component re-renders. Excluded from deps to keep the linter calm.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realEstateProperties]);

  const mortgagesUsd = mortgageSlicesData.reduce(
    (s, m) => s.plus(m.currentBalanceUsd), ZERO,
  );

  // Debt items: balances as stored. Manual updates only (no auto-roll).
  const debtUsd = debtItems.reduce((s, d) => s.plus(d.balanceUsd), ZERO);

  // ---- aggregate totals ----
  const totalAssetsUsd = directUsd
    .plus(etfUsd)
    .plus(securitiesUsd)
    .plus(metalsUsd)
    .plus(realEstateUsd)
    .plus(cashUsd);

  const totalLiabilitiesUsd = btcLoanUsd
    .plus(mortgagesUsd)
    .plus(debtUsd);

  const netWorthUsd = totalAssetsUsd.minus(totalLiabilitiesUsd);

  // ---- assemble Assets pie slices ----
  // Order: BTC -> Bitcoin ETFs (largest first) -> securities (largest first)
  // -> metals (largest first) -> real estate (by property index) -> cash.
  // This order matters because colors are assigned by display index.
  const assetSlices: AssetSlice[] = useMemo(() => {
    const out: AssetSlice[] = [];
    if (directUsd.greaterThan(0)) {
      out.push({
        id: 'btc-direct',
        name: 'Bitcoin (direct)',
        category: 'btc',
        value: directUsd.toNumber(),
        btcAmount: directBtc,
        pricePerUnit: btcPriceUsd,
      });
    }
    const etfSlices: AssetSlice[] = [];
    for (const h of holdings) {
      const pos = holdingPosition(h, txs);
      const valueDec = pos.currentShares.times(pos.effectivePriceUsd);
      if (valueDec.lessThanOrEqualTo(0)) continue;
      const btcEq = btcPriceUsd.isZero() ? ZERO : valueDec.div(btcPriceUsd);
      etfSlices.push({
        id: `etf-${h.id}`,
        name: h.name,
        category: 'btc_etf',
        subKey: h.symbol,
        value: valueDec.toNumber(),
        btcAmount: btcEq,
        quantity: pos.currentShares,
        quantityUnit: 'shares',
        pricePerUnit: pos.effectivePriceUsd,
      });
    }
    etfSlices.sort((a, b) => b.value - a.value);
    out.push(...etfSlices);
    out.push(...[...securityPieData].sort((a, b) => b.value - a.value));
    out.push(...[...metalPieData].sort((a, b) => b.value - a.value));
    realEstateProperties.forEach((p, idx) => {
      if (p.currentValueUsd.lessThanOrEqualTo(0)) return;
      out.push({
        id: `re-${p.id}`,
        name: p.label || 'Property',
        category: 'real_estate',
        subKey: String(idx),
        value: p.currentValueUsd.toNumber(),
      });
    });
    for (const c of cashEntries) {
      if (c.amountUsd.lessThanOrEqualTo(0)) continue;
      out.push({
        id: `cash-${c.id}`,
        name: c.label || 'Cash',
        category: 'cash',
        value: c.amountUsd.toNumber(),
      });
    }
    return out;
  }, [
    directBtc, directUsd, btcPriceUsd, holdings, txs,
    securityPieData, metalPieData, realEstateProperties, cashEntries,
  ]);

  // ---- color the assets slices ----
  const assetColors = useMemo(() => {
    let btcEtfIdx = 0;
    let grayscaleIdx = 0;
    return assetSlices.map(s => {
      switch (s.category) {
        case 'btc':
          return 'var(--accent)';
        case 'btc_etf':
          return getBitcoinEtfColor(btcEtfIdx++);
        case 'security':
          // Grayscale by display order, cycling through the palette so
          // a 12th security gets the same shade as the 4th (12 mod 8 = 4).
          // Order is by display index in the slices array, recomputed
          // on every render per Paul's spec.
          return GRAYSCALE_SHADES[grayscaleIdx++ % GRAYSCALE_SHADES.length] as string;
        case 'metal':
          // Per-metal native color. Spot tickers and metal ETFs both
          // resolve via metalKey() at slice-build time.
          return METAL_COLOR_BY_KEY[s.subKey ?? 'silver'] ?? METAL_COLOR_BY_KEY['silver'] as string;
        case 'real_estate':
          // Beige ramp; index taken from the subKey we stored when
          // building the slice (which matches realEstateProperties array
          // position, so the same color matches in the Liabilities pie).
          return BEIGE_SHADES[Number(s.subKey ?? 0) % BEIGE_SHADES.length] as string;
        case 'cash':
          // Pure white per spec. Outline strokes against the dark bg
          // keep the slice visible.
          return '#ffffff';
      }
    });
  }, [assetSlices]);

  // ---- assemble Liabilities pie slices ----
  const liabilitySlices: LiabilitySlice[] = useMemo(() => {
    const out: LiabilitySlice[] = [];
    if (btcLoanUsd.greaterThan(0)) {
      out.push({
        id: 'btc-loan',
        name: 'Bitcoin loan',
        category: 'btc_loan',
        value: btcLoanUsd.toNumber(),
        aprPct: loanState.currentApr,
      });
    }
    for (const m of mortgageSlicesData) {
      out.push({
        id: `mort-${m.property.id}`,
        name: `Mortgage: ${m.property.label || 'Property'}`,
        category: 'mortgage',
        propertyIdx: m.propertyIdx,
        value: m.currentBalanceUsd.toNumber(),
        aprPct: m.property.mortgage?.aprPct,
      });
    }
    for (const d of debtItems) {
      if (d.balanceUsd.lessThanOrEqualTo(0)) continue;
      out.push({
        id: `debt-${d.id}`,
        name: d.label || debtTypeLabel(d.type),
        category: 'debt',
        debtType: d.type,
        value: d.balanceUsd.toNumber(),
        aprPct: d.aprPct,
      });
    }
    return out;
  }, [btcLoanUsd, loanState.currentApr, mortgageSlicesData, debtItems]);

  const liabilityColors = useMemo(() => {
    let debtIdx = 0;
    return liabilitySlices.map(s => {
      switch (s.category) {
        case 'btc_loan':
          return BTC_LOAN_COLOR;
        case 'mortgage':
          // Match this mortgage's property beige shade in the Assets pie.
          return BEIGE_SHADES[(s.propertyIdx ?? 0) % BEIGE_SHADES.length] as string;
        case 'debt':
          // Red ramp by display order.
          return RED_SHADES[debtIdx++ % RED_SHADES.length] as string;
      }
    });
  }, [liabilitySlices]);

  // Patch DL: activeAssetIdx + activeLiabIdx state moved into the
  // chart components (NetWorthAssetsChart / NetWorthLiabilitiesChart)
  // so they can manage their own hover behavior without threading
  // setters through the lazy boundary.

  // Net worth headline only renders a number once SOMETHING is in scope
  // (BTC price loaded, or non-zero cash/real-estate that don't need a
  // price source). Avoids showing a misleading "$0" before the live BTC
  // snapshot lands on first paint.
  const haveAnyValue = havePrice
    || cashUsd.greaterThan(0)
    || realEstateUsd.greaterThan(0)
    || securitiesUsd.greaterThan(0)
    || metalsUsd.greaterThan(0);

  return (
    <section className="section" style={{ paddingTop: 'var(--gap-m)' }}>
      <p className="section-eyebrow">Net worth</p>

      {/* ---- Net worth headline + equation tile ---- */}
      <div style={{ marginBottom: 'var(--gap-l)' }}>
        <div className="mono" style={{ fontSize: 'var(--fs-display)', fontWeight: 500 }}>
          {haveAnyValue ? <>${formatUsd(netWorthUsd, { decimals: 0 })}</> : '-'}
        </div>
        <div className="faint mono" style={{ fontSize: 'var(--fs-small)', marginTop: 4 }}>
          Assets ${formatUsd(totalAssetsUsd, { decimals: 0 })}
          {' '}-{' '}
          Liabilities ${formatUsd(totalLiabilitiesUsd, { decimals: 0 })}
        </div>
        {havePrice ? (
          <div className="faint mono" style={{ fontSize: 'var(--fs-micro)', marginTop: 2 }}>
            BTC stack: {formatBtc(directBtc, 4)} BTC
            {etfUsd.greaterThan(0) && !btcPriceUsd.isZero()
              ? <> {'\u00B7'} ETF equivalent: {formatBtc(etfUsd.div(btcPriceUsd), 4)} BTC</>
              : null}
          </div>
        ) : null}
      </div>

      {/* ---- Assets sub-card ---- */}
      <div style={{
        marginTop: 'var(--gap-m)',
        paddingTop: 'var(--gap-m)',
        borderTop: '1px solid var(--rule)',
      }}>
        <div className="row between" style={{ alignItems: 'baseline', marginBottom: 'var(--gap-s)' }}>
          <h3 className="serif" style={{ margin: 0, fontSize: 'var(--fs-lead)', fontWeight: 400 }}>
            Assets
          </h3>
          <div className="mono" style={{ fontSize: 'var(--fs-base)', fontWeight: 500 }}>
            ${formatUsd(totalAssetsUsd, { decimals: 0 })}
          </div>
        </div>
        {assetSlices.length === 0 ? (
          <p className="muted" style={{ fontStyle: 'italic' }}>
            No assets tracked yet. Add Bitcoin purchases or entries under Other in Positions.
          </p>
        ) : (
          <Suspense
            fallback={
              <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <p className="muted" style={{ fontSize: 'var(--fs-small)' }}>Loading chart{'\u2026'}</p>
              </div>
            }
          >
            <LazyAssetsChart slices={assetSlices} colors={assetColors} />
          </Suspense>
        )}
      </div>

      {/* ---- Liabilities sub-card ---- */}
      <div style={{
        marginTop: 'var(--gap-m)',
        paddingTop: 'var(--gap-m)',
        borderTop: '1px solid var(--rule)',
      }}>
        <div className="row between" style={{ alignItems: 'baseline', marginBottom: 'var(--gap-s)' }}>
          <h3 className="serif" style={{ margin: 0, fontSize: 'var(--fs-lead)', fontWeight: 400 }}>
            Liabilities
          </h3>
          <div className="mono" style={{ fontSize: 'var(--fs-base)', fontWeight: 500, color: 'var(--neg)' }}>
            ${formatUsd(totalLiabilitiesUsd, { decimals: 0 })}
          </div>
        </div>
        {liabilitySlices.length === 0 ? (
          <p className="muted" style={{ fontStyle: 'italic' }}>
            No liabilities tracked. Add a Bitcoin loan event, a mortgage on a property, or a debt item under Other in Positions.
          </p>
        ) : (
          <Suspense
            fallback={
              <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <p className="muted" style={{ fontSize: 'var(--fs-small)' }}>Loading chart{'\u2026'}</p>
              </div>
            }
          >
            <LazyLiabilitiesChart slices={liabilitySlices} colors={liabilityColors} />
          </Suspense>
        )}
      </div>
    </section>
  );
}
